import cn from 'classnames'
import React, { FC } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { CommerceProvider } from '@framework'
import { useUI } from '@components/ui/context'
import type { Page } from '@commerce/types/page'
import { Navbar } from '@components/common'

const Footer = dynamic<{ settings?: any; color?: string; className?: string }>(
  () => import('@components/common/Footer').then((m) => m.default) as any,
  { ssr: true }
)
import type { Category } from '@commerce/types/site'
import { Modal, Sidebar } from '@components/ui'
import s from './Layout.module.css'
import { LineItem } from '@commerce/types/cart'
import { CancelReason, type IConfirmCancelSubscriptionViewProps } from '@components/ui/ConfirmCancelSubscription/ConfirmCancelSubscriptionView'
import { urlFor } from '@lib/sanity'
import type { IUpsellingViewProps } from '@components/upselling/UpsellingView/UpsellingView'

const ProfileSidebarView = dynamic(() => import('@components/profile/ProfileSidebarView').then((m) => m.default) as any, { ssr: false })
const HowToSidebarView = dynamic(() => import('@components/ui/HowToSidebarView').then((m) => m.default) as any, { ssr: false })
const ProductInfoSidebarView = dynamic(() => import('@components/ui/ProductInfoSidebarView').then((m) => m.default) as any, { ssr: false })
const ProductInfoSidebarView2 = dynamic(() => import('@components/ui/ProductInfoSidebarView2').then((m) => m.default) as any, { ssr: false })
const SubscriptionBenefitSidebarView = dynamic(() => import('@components/ui/SubscriptionBenefitSidebarView').then((m) => m.default) as any, { ssr: false })
const UpsellingView = dynamic<IUpsellingViewProps>(() => import('@components/upselling/UpsellingView').then((m) => m.default) as any, { ssr: false })
const CartConflictView = dynamic<{ conflictType: 'subscription' | 'one-time' }>(
  () => import('@components/ui/CartConflict').then((m) => m.default) as any,
  { ssr: false }
)
const ConfirmCancelSubscriptionView = dynamic<IConfirmCancelSubscriptionViewProps>(
  () => import('@components/ui/ConfirmCancelSubscription').then((m) => m.default) as any,
  { ssr: false }
)
const Maintenance = dynamic(() => import('@components/maintenance').then((m) => m.default) as any, { ssr: true })

interface Props {
  children?: React.ReactNode
  pageProps: {
    page?: Page[]
    showPromo: boolean
    global: {
      mainNavigation: any
      settings: any
    }
    upselling: any
    cartRecommendations: any
    allProducts: any
    categories: Category[]
    product: any
    productData: any
    clean?: boolean
    noHeader?: boolean
    noFooter?: boolean
  }
}


/**
 * SIDEBAR
 */

const SidebarView: FC<{ sidebarView: string; closeSidebar(): any }> = ({
                                                                         sidebarView,
                                                                         closeSidebar
                                                                       }) => {
  return (
    <Sidebar onClose={closeSidebar}>
      {sidebarView === 'PROFILE_VIEW' && <ProfileSidebarView />}
      {sidebarView === 'HOW_TO' && <HowToSidebarView />}
      {sidebarView === 'PRODUCT_INFO' && <ProductInfoSidebarView />}
      {sidebarView === 'PRODUCT_INFO2' && <ProductInfoSidebarView2 />}
      {sidebarView === 'SUBSCRIPTION_BENEFITS' && <SubscriptionBenefitSidebarView />}
    </Sidebar>
  )
}

const SidebarUI: FC = () => {
  const { displaySidebar, closeSidebar, sidebarView } = useUI()
  return displaySidebar ? (
    <SidebarView sidebarView={sidebarView} closeSidebar={closeSidebar} />
  ) : null
}


/**
 * MODAL
 */

const ModalView: FC<{
  modalView: string;
  closeModal(): any,
  hideCloseButton?: boolean,
  modalMobileFullscreen?: boolean
  infoOnly?: boolean,
  parametersStep?: boolean,
  shippingStep?: boolean,
  subscriptionStep?: boolean,
  subscriptionConfig?: any,
  productUpsell?: boolean,
  parentKitId?: string,
  upsellingItems?: {
    parametersHeading?: string,
    parametersSubheading?: string,
    parameters?: {
      _id: string
      title: string
      subtitle: string
      description: string
      image: any
      productSlug?: string
      variantSlug?: string
      originalPrice: string
      discountedPrice: string
    }[],
    shippingMethodsHeading?: string
    shippingMethods?: {
      _id: string
      title: string
      type: string
      heading: string
      price: string
      description: string
      colorClass: string
      textColorClass: string
      features: {
        icon: string
        description: string
      }[]
    }[]
  },
  product?: any,
  lineItem?: LineItem,
  subscriptionCancelReasons?: CancelReason[]
}> = ({
        modalView,
        closeModal,
        hideCloseButton,
        modalMobileFullscreen,
        infoOnly,
        parametersStep,
        shippingStep,
        subscriptionStep,
        subscriptionConfig,
        productUpsell,
        parentKitId,
        upsellingItems,
        product,
        lineItem,
        subscriptionCancelReasons
      }) => {
  return (
    <Modal onClose={closeModal} hideCloseButton={hideCloseButton} modalMobileFullscreen={modalMobileFullscreen}
           focusFirst={false}>
      {modalView === 'UPSELL_VIEW' &&
        <UpsellingView infoOnly={infoOnly} parametersStep={parametersStep} shippingStep={shippingStep}
                       subscriptionStep={subscriptionStep} subscriptionConfig={subscriptionConfig}
                       productUpsell={productUpsell} parentKitId={parentKitId} upsellingItems={upsellingItems}
                       product={product}
                       lineItem={lineItem} />}
      {modalView === 'CART_CONFLICT_VIEW_SUBSCRIPTION' && <CartConflictView conflictType='subscription' />}
      {modalView === 'CART_CONFLICT_VIEW_ONE_TIME' && <CartConflictView conflictType='one-time' />}
      {modalView === 'CONFIRM_CANCEL_SUBSCRIPTION' &&
        <ConfirmCancelSubscriptionView cancelReasons={subscriptionCancelReasons} />}
    </Modal>
  )
}

const ModalUI: FC = () => {
  const {
    displayModal,
    closeModal,
    modalHideCloseButton,
    modalMobileFullscreen,
    modalView,
    upsellInfoOnly,
    upsellParametersStep,
    upsellShippingStep,
    upsellSubscriptionStep,
    upsellSubscriptionConfig,
    upsellProductUpsell,
    upsellParentKitId,
    upsellingItems,
    upsellProduct,
    upsellLineItem,
    subscriptionCancelReasons
  } = useUI()
  return displayModal ? (
    <ModalView modalView={modalView} closeModal={closeModal} hideCloseButton={modalHideCloseButton}
               modalMobileFullscreen={modalMobileFullscreen} infoOnly={upsellInfoOnly}
               parametersStep={upsellParametersStep} shippingStep={upsellShippingStep}
               subscriptionStep={upsellSubscriptionStep}
               productUpsell={upsellProductUpsell} parentKitId={upsellParentKitId} upsellingItems={upsellingItems}
               product={upsellProduct} lineItem={upsellLineItem} subscriptionConfig={upsellSubscriptionConfig}
               subscriptionCancelReasons={subscriptionCancelReasons} />
  ) : null
}


/**
 * LAYOUT
 */

const Layout: FC<Props> = ({
                             children,
                             pageProps: { categories = [], showPromo = false, ...pageProps }
                           }) => {
  const { locale = 'de', pathname } = useRouter()
  // @ts-ignore
  let current = pageProps?.page?.handle?.current
  let product = pageProps?.product
  let productData = pageProps?.productData
  const clean = pageProps?.clean
  let result = pathname.includes('activateKits')
  const maintenance = process.env.NEXT_PUBLIC_MAINTENANCE_MODE === 'true'

  return (
    <CommerceProvider locale={locale}>
      <div className={cn(s.root)}>
        {pageProps?.global && !result && !pageProps?.noHeader && (
          <Navbar
            className={(current === 'home' || current === '404' || product || clean) ? 'clean' : 'shadow-header'}
            settings={pageProps?.global}
            colorLight={productData?.color_light}
            colorDark={productData?.color_dark}
            coLogo={urlFor(productData?.coLogo).url()}
            banner={(!!(current === 'home' || current === 'cart' || product))}
          />
        )}
        {maintenance && !(current === 'home' || current === 'cart' || product) ?
          (
            <Maintenance></Maintenance>
          )
          :
          (
            <main><div suppressHydrationWarning>{children}</div></main>
          )
        }
        {pageProps?.global?.settings && !result && (!pageProps?.noFooter || maintenance) && !(current === 'cart') && <
          Footer settings={pageProps?.global} color={productData?.color_dark} />}
        <SidebarUI />
        <ModalUI />
      </div>
    </CommerceProvider>
  )
}

export default Layout
